import { formatTimeForDisplay, getTimezoneDisplayName } from '../lib/formatUtils';

// Unit utility functions for handling metric/imperial conversions
export const getUnitFromSettings = (): string => {
  try {
    const settings = localStorage.getItem('settings');
    if (settings) {
      const settingsData = JSON.parse(settings);
      const unit = settingsData.general?.unit || 'Imperial';
      // Convert from settings format to internal format
      return unit === 'Metric' ? 'mm' : 'in';
    }
  } catch (error) {
  }
  return 'in'; // Default fallback
};

export const getUnitLabel = (unit: string): string => {
  switch (unit) {
    case 'mm':
      return 'mm';
    case 'Inches':
      return 'in';
    default:
      return 'in';
  }
};

export const getUnitDisplayText = (unit: string): string => {
  switch (unit) {
    case 'mm':
      return 'Metric (mm)';
    case 'Inches':
      return 'Imperial (in)';
    default:
      return 'Metric (mm)';
  }
};

// Helper function to round to 4 decimal places
export const roundToTwoDecimalPlaces = (value: number): number => {
  return Math.round(value * 10000) / 10000;
};

/** Round to 4 decimal places for unit conversion */
const roundTo4Decimals = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return Math.round(value * 10000) / 10000;
};

/** Round HiLo threshold to 2 decimal places (mm storage / display). */
export const roundHiLoThreshold = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return Math.round(value * 100) / 100;
};

/**
 * Convert value from user's unit to inches (for database save).
 * DB always stores in inches.
 */
export const convertValueToInches = (value: number, unit: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  const v = Number(value);
  switch (unit) {
    case 'in': return roundTo4Decimals(v);
    case 'cm': return roundTo4Decimals(v / 2.54);
    case 'mm': return roundTo4Decimals(v / 25.4);
    case 'ft': return roundTo4Decimals(v * 12);
    case 'm': return roundTo4Decimals(v * 39.3701);
    default: return roundTo4Decimals(v);
  }
};

/**
 * Convert value from inches (database) to user's display unit.
 */
export const convertValueFromInches = (value: number, unit: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  const v = Number(value);
  switch (unit) {
    case 'in': return roundTo4Decimals(v);
    case 'cm': return roundTo4Decimals(v * 2.54);
    case 'mm': return roundTo4Decimals(v * 25.4);
    case 'ft': return roundTo4Decimals(v / 12);
    case 'm': return roundTo4Decimals(v / 39.3701);
    default: return roundTo4Decimals(v);
  }
};

/** Convert a value from any unit to mm. Used for pipefitter length fields (API expects mm). */
export const convertValueToMm = (value: number, unit: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  const v = Number(value);
  switch (unit) {
    case 'mm': return roundTo4Decimals(v);
    case 'in': return roundTo4Decimals(v * 25.4);
    case 'cm': return roundTo4Decimals(v * 10);
    case 'ft': return roundTo4Decimals(v * 304.8);
    case 'm':  return roundTo4Decimals(v * 1000);
    default:   return roundTo4Decimals(v);
  }
};

/** Convert a value from mm to any display unit. Used for pipefitter length fields loaded from API. */
export const convertValueFromMm = (valueInMm: number, unit: string): number => {
  if (typeof valueInMm !== 'number' || isNaN(valueInMm)) return 0;
  const v = Number(valueInMm);
  switch (unit) {
    case 'mm': return roundTo4Decimals(v);
    case 'in': return roundTo4Decimals(v / 25.4);
    case 'cm': return roundTo4Decimals(v / 10);
    case 'ft': return roundTo4Decimals(v / 304.8);
    case 'm':  return roundTo4Decimals(v / 1000);
    default:   return roundTo4Decimals(v);
  }
};

/** All pipefitter length fields that are stored in mm by the API.
 *  NOTE: nominalPipeLength and targetPipelineLength are stored in ft (no conversion) and are excluded here. */
export const PIPEFITTER_MM_FIELDS = [
  'toeSeamSeparationMinMm', 'seamCapWidthMaxMm', 'hiloWpsLimitMm',
  'hiloPreferredLimitTopMm', 'hiloPreferredLimitMiddleMm', 'hiloPreferredLimitLowerMm',
  'pipeInnerDiameter',
  'nominalOd', 'nominalId', 'hiloThreshold',
  'nominalOdMin', 'nominalOdMax', 'nominalIdMin', 'nominalIdMax',
  'oORIDTolerance', 'oORODTolerance',
  'oORIDMin', 'oORIDMax', 'oORODMin', 'oORODMax',
] as const;

/** Fields always stored/displayed in mm — never convert via pipefitterUnit fallback. */
const PIPEFITTER_MM_ONLY_FIELDS = new Set([
  'hiloThreshold',
  'nominalOd',
]);

/** Convert all pipefitter mm fields in a pipe object from display unit → mm (for API save).
 *  Uses per-field unit from pipefitterFieldUnits if set, else falls back to pipefitterUnit, else 'mm'.
 *  hiLoThreshold / nominalOd are always mm (no conversion). */
export const convertPipefitterFieldsToMm = (pipe: any): any => {
  if (!pipe || typeof pipe !== 'object') return pipe;
  const fieldUnits: Record<string, string> = pipe.units?.pipefitterFieldUnits ?? {};
  const fallbackUnit: string = pipe.units?.pipefitterUnit ?? 'mm';
  const updated: any = { ...pipe };
  let changed = false;
  for (const field of PIPEFITTER_MM_FIELDS) {
    const v = pipe[field];
    if (v == null || v === '' || Number(v) === 0) continue;

    if (PIPEFITTER_MM_ONLY_FIELDS.has(field)) {
      const n = Number(v);
      updated[field] = field === 'hiloThreshold' ? roundHiLoThreshold(n) : n;
      // Force unit metadata to mm so load path never re-converts
      if (!updated.units) updated.units = { ...(pipe.units ?? {}) };
      updated.units = {
        ...updated.units,
        pipefitterFieldUnits: {
          ...(updated.units.pipefitterFieldUnits ?? fieldUnits),
          [field]: 'mm',
        },
      };
      changed = true;
      continue;
    }

    const unit = fieldUnits[field] ?? fallbackUnit;
    if (unit === 'mm') continue;
    updated[field] = convertValueToMm(Number(v), unit);
    changed = true;
  }
  return changed ? updated : pipe;
};

/** Convert all pipefitter mm fields in a pipe object from mm → display unit (for loading from API).
 *  Uses per-field unit from pipefitterFieldUnits if set, else falls back to pipefitterUnit, else 'mm'.
 *  hiLoThreshold / nominalOd are always mm (no conversion). */
export const convertPipefitterFieldsFromMm = (pipe: any): any => {
  if (!pipe || typeof pipe !== 'object') return pipe;
  const fieldUnits: Record<string, string> = pipe.units?.pipefitterFieldUnits ?? {};
  const fallbackUnit: string = pipe.units?.pipefitterUnit ?? 'mm';
  const updated: any = { ...pipe };
  let changed = false;
  for (const field of PIPEFITTER_MM_FIELDS) {
    const v = pipe[field];
    if (v == null || v === '' || Number(v) === 0) continue;

    if (PIPEFITTER_MM_ONLY_FIELDS.has(field)) {
      const n = Number(v);
      updated[field] = field === 'hiloThreshold' ? roundHiLoThreshold(n) : n;
      if (!updated.units) updated.units = { ...(pipe.units ?? {}) };
      updated.units = {
        ...updated.units,
        pipefitterFieldUnits: {
          ...(updated.units.pipefitterFieldUnits ?? fieldUnits),
          [field]: 'mm',
        },
      };
      changed = true;
      continue;
    }

    const unit = fieldUnits[field] ?? fallbackUnit;
    if (unit === 'mm') continue;
    updated[field] = convertValueFromMm(Number(v), unit);
    changed = true;
  }
  return changed ? updated : pipe;
};

// Convert values between units if needed
export const convertValue = (value: number, fromUnit: string, toUnit: string): number => {
  if (fromUnit === toUnit) return value;
  
  // Convert mm to inches
  if (fromUnit === 'mm' && toUnit === 'Inches') {
    return value / 25.4;
  }
  
  // Convert inches to mm
  if (fromUnit === 'Inches' && toUnit === 'mm') {
    return value * 25.4;
  }
  
  return roundToTwoDecimalPlaces(value);  //round to 2 decimal places for display
};

// Convert weld data value from inches (API base) to display unit based on settings
export const convertWeldDataValue = (value: number, parameter?: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from inches to mm
  if (displayUnit === 'mm') {
    // For energy parameters (heat, trueEnergy), convert from kJ/in to kJ/mm
    if (parameter === 'heat' || parameter === 'trueEnergy') {
      return value / 25.4; // Convert kJ/in to kJ/mm
    }
    // For physical measurements (travelSpeed, wireSpeed, totalWireConsumed, etc.), convert from inches to mm
    if (parameter === 'travelSpeed' || parameter === 'wireSpeed' || parameter === 'totalWireConsumed' || parameter === 'oscillationWidth' || parameter === 'distance') {
      return value * 25.4;
    }
    // For other parameters, convert from inches to mm
    return value * 25.4;
  }
  
  // If display unit is inches, return as is (no conversion needed)
  return roundToTwoDecimalPlaces(value);  //round to 2 decimal places for display
};

// Convert setup value from inches (API base) to display unit based on settings
export const convertSetupValue = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from inches to mm and round to 3 decimal places
  if (displayUnit === 'mm') {
    return Math.round(value * 25.4 * 1000) / 1000;
  }
  
  // If display unit is inches, return same value rounded to 4 decimal places
  return Math.round(value * 10000) / 10000;
};

// Convert setup value back to inches for API calls
export const convertSetupValueToInches = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from mm to inches and round to 4 decimal places
  if (displayUnit === 'mm') {
    return Math.round((value / 25.4) * 10000) / 10000;
  }
  
  // If display unit is inches, return same value rounded to 4 decimal places
  return Math.round(value * 10000) / 10000;
};

// Format weld data value with proper unit label for display
export const formatWeldDataValue = (value: number, parameter?: string): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  
  const displayUnit = getUnitFromSettings();
  const convertedValue = convertWeldDataValue(value, parameter);
  const unitLabel = getUnitLabel(displayUnit);
  
  // Round to 2 decimal places for display
  const roundedValue = Math.round(convertedValue * 100) / 100;
  
  // For specific parameters, you might want different formatting
  if (parameter) {
    switch (parameter) {
      case 'travel_speed':
        return `${roundedValue} ${unitLabel}/min`;
      case 'wire_speed':
        return `${roundedValue} ${unitLabel}/min`;
      case 'oscillation_width':
        return `${roundedValue} ${unitLabel}`;
      case 'total_wire_consumed':
        return `${roundedValue} ${unitLabel}`;
      case 'heat':
      case 'true_energy':
        return `${roundedValue} kJ/${unitLabel}`;
      default:
        return `${roundedValue} ${unitLabel}`;
    }
  }
  
  return `${roundedValue} ${unitLabel}`;
};

// Format setup value with proper unit label for display
export const formatSetupValue = (value: number): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  
  const displayUnit = getUnitFromSettings();
  const convertedValue = convertSetupValue(value);
  const unitLabel = getUnitLabel(displayUnit);
  
  // Round to 2 decimal places for display
  const roundedValue = Math.round(convertedValue * 100) / 100;
  
  return `${roundedValue} ${unitLabel}`;
};

// Convert weld data object with all values from inches to display units
export const convertWeldDataObject = (weldData: any): any => {
  if (!weldData || typeof weldData !== 'object') return weldData;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is inches, no conversion needed
  if (displayUnit === 'Inches') {
    return weldData;
  }
  
  // Parameters that need conversion from inches to mm
  const convertibleParameters = [
    'travel_speed',
    'wire_speed', 
    'oscillation_width',
    'total_wire_consumed',
    'heat',
    'true_energy'
  ];
  
  const convertedData = { ...weldData };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertWeldDataValue(convertedData[param], param);
    }
  });
  
  return convertedData;
};

// Convert setup pipe object with all values from inches to display units
export const convertSetupPipeObject = (pipeData: any): any => {
  if (!pipeData || typeof pipeData !== 'object') return pipeData;
  
  const displayUnit = getUnitFromSettings();
  const convertfeetToInchesParamerts = [
    'pipeLength',
    'outputAssemblyLength'
  ]
  // If display unit is inches, return same object (no conversion needed)
  if (displayUnit === 'Inches') {
    convertfeetToInchesParamerts.forEach(param => {
      if (pipeData[param] !== undefined && typeof pipeData[param] === 'number') {
        pipeData[param] = convertInchesToFeet(pipeData[param]);
      }
    });
    return pipeData;
  }
  
  // Parameters that need conversion from inches to mm
  const convertibleParameters = [
    'pipeSize',
    'wallThickness',
   
  ];
  
  const convertedData = { ...pipeData };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertSetupValue(convertedData[param]);
    }
  });
  
  return convertedData;
};

// Convert setup pipe object back to inches for API calls
export const convertSetupPipeObjectToInches = (pipeData: any): any => {
  if (!pipeData || typeof pipeData !== 'object') return pipeData;
  
  const displayUnit = getUnitFromSettings();
  const convertfeetToInchesParamerts = [
    'pipeLength',
    'outputAssemblyLength'
  ]
  // If display unit is inches, return same object (no conversion needed)
  if (displayUnit === 'Inches') {
    convertfeetToInchesParamerts.forEach(param => {
      if (pipeData[param] !== undefined && typeof pipeData[param] === 'number') {
        pipeData[param] = convertFeetToInches(pipeData[param]);
      }
    });
    return pipeData;
  }
  
  // Parameters that need conversion from mm to inches
  const convertibleParameters = [
    'pipeSize',
    'wallThickness',
  
  ];
  

  const convertedData = { ...pipeData };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertSetupValueToInches(convertedData[param]);
    }  });
   
  return convertedData;
};

export const convertFeetToInches = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return roundToTwoDecimalPlaces(value * 12);
};
export const convertInchesToFeet = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return roundToTwoDecimalPlaces(value / 12);
};

/** Convert length from feet (canonical) to display unit. 1 ft = 0.3048 m */
export const convertLengthFromFt = (valueFt: number, toUnit: 'ft' | 'm'): number => {
  if (typeof valueFt !== 'number' || isNaN(valueFt)) return 0;
  if (toUnit === 'ft') return roundTo4Decimals(valueFt);
  return roundTo4Decimals(valueFt * 0.3048);
};

/** Convert length from display unit to feet (canonical). */
export const convertLengthToFt = (value: number, fromUnit: 'ft' | 'm'): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  if (fromUnit === 'ft') return roundTo4Decimals(value);
  return roundTo4Decimals(value / 0.3048);
};

/** Convert length from mm (canonical) to display unit. */
export const convertLengthFromMm = (valueMm: number, toUnit: 'mm' | 'in'): number => {
  if (typeof valueMm !== 'number' || isNaN(valueMm)) return 0;
  if (toUnit === 'mm') return roundTo4Decimals(valueMm);
  return roundTo4Decimals(valueMm / 25.4);
};

/** Convert length from display unit to mm (canonical). */
export const convertLengthToMm = (value: number, fromUnit: 'mm' | 'in'): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  if (fromUnit === 'mm') return roundTo4Decimals(value);
  return roundTo4Decimals(value * 25.4);
};

/** Default pipe unit object when not saved */
const DEFAULT_PIPE_UNIT = { pipeSize: 'in', wallThickness: 'in', length: 'in', outputAssemblyLength: 'ft' };

/** Decimal places for setup pipe size / wall thickness: mm → 3, inch (and other units) → 4. */
export const getSetupDimensionDecimalPlaces = (unit: string): number =>
  unit === 'mm' ? 3 : 4;

/** Round pipe size / wall thickness for display or before unit conversion. */
export const roundSetupDimensionForUnit = (value: number, unit: string): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  const dp = getSetupDimensionDecimalPlaces(unit);
  const factor = 10 ** dp;
  const rounded = Math.round(value * factor) / factor;
  if (unit === 'mm') {
    return snapMmDisplayFromInchConversion(rounded);
  }
  return rounded;
};

/**
 * Undo mm display noise from inch DB precision (e.g. 0.689 in → 17.501 mm → 17.5 mm).
 * Uses integer-scaled diff so float noise (17.501 - 17.5 > 0.001) does not block snap.
 */
const snapMmDisplayFromInchConversion = (mm: number): number => {
  const rounded = Math.round(mm * 1000) / 1000;
  const roundedScaled = Math.round(rounded * 1000);
  for (let dp = 0; dp <= 2; dp++) {
    const factor = 10 ** dp;
    const snapped = Math.round(rounded * factor) / factor;
    const diffScaled = Math.abs(roundedScaled - Math.round(snapped * 1000));
    if (diffScaled <= 1) {
      return snapped;
    }
  }
  return rounded;
};

/** Format setup dimension for UI (trim trailing zeros; mm/in decimal limits). */
export const formatSetupDimensionForDisplay = (value: number, unit: string): string => {
  if (typeof value !== 'number' || isNaN(value) || value === 0) return '';
  const dp = getSetupDimensionDecimalPlaces(unit);
  return value.toFixed(dp).replace(/\.?0+$/, '');
};

/** Inches stored with 6 dp so 3-decimal mm values round-trip correctly. */
const roundInchesForSetupStorage = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  return Math.round(value * 1_000_000) / 1_000_000;
};

const dimensionToInchesRaw = (value: number, unit: string): number => {
  switch (unit) {
    case 'in': return value;
    case 'cm': return value / 2.54;
    case 'mm': return value / 25.4;
    case 'ft': return value * 12;
    case 'm': return value * 39.3701;
    default: return value;
  }
};

const dimensionFromInchesRaw = (valueInches: number, unit: string): number => {
  switch (unit) {
    case 'in': return valueInches;
    case 'cm': return valueInches * 2.54;
    case 'mm': return valueInches * 25.4;
    case 'ft': return valueInches / 12;
    case 'm': return valueInches / 39.3701;
    default: return valueInches;
  }
};

/** Convert pipe size / wall thickness from display unit → inches for DB (pipeSize, wallThickness only). */
export const convertSetupDimensionToInches = (value: number, unit: string): number => {
  const rounded = roundSetupDimensionForUnit(Number(value) || 0, unit);
  return roundInchesForSetupStorage(dimensionToInchesRaw(rounded, unit || 'in'));
};

/** Convert pipe size / wall thickness from inches (DB) → display unit (pipeSize, wallThickness only). */
export const convertSetupDimensionFromInches = (valueInches: number, unit: string): number => {
  const u = unit || 'in';
  const display = dimensionFromInchesRaw(Number(valueInches) || 0, u);
  return roundSetupDimensionForUnit(display, u);
};

/**
 * Convert pipe values from user's display units to inches for database save.
 * DB always stores pipeSize, wallThickness, pipeLength, outputAssemblyLength in inches.
 * nominalPipeLength / targetPipelineLength are ft-only (stored as entered, no conversion).
 * API expects `units` (not `unit`).
 */
export const convertSetupPipeToInchesForSave = (pipe: any): any => {
  if (!pipe || typeof pipe !== 'object') return pipe;
  const u = pipe.unit || pipe.units || DEFAULT_PIPE_UNIT;
  const { unit, ...rest } = pipe;
  const pfFieldUnits: Record<string, string> = {
    ...(u.pipefitterFieldUnits ?? {}),
    nominalPipeLength: 'ft',
    targetPipelineLength: 'ft',
    /** mm-only — never convert via pipefitterUnit (avoids 516→13106 when fallback is in). */
    nominalOd: 'mm',
    hiloThreshold: 'mm',
  };
  return {
    ...rest,
    units: { ...u, pipefitterFieldUnits: pfFieldUnits },
    pipeSize: convertSetupDimensionToInches(Number(pipe.pipeSize) || 0, u.pipeSize || 'in'),
    wallThickness: convertSetupDimensionToInches(Number(pipe.wallThickness) || 0, u.wallThickness || 'in'),
    pipeLength: convertValueToInches(Number(pipe.pipeLength) || 0, u.length || 'in'),
    outputAssemblyLength: convertValueToInches(Number(pipe.outputAssemblyLength) || 0, u.outputAssemblyLength || 'ft'),
    // Length targets: ft only — pass through as entered
    ...(pipe.targetPipelineLength != null
      ? { targetPipelineLength: Number(pipe.targetPipelineLength) || 0 }
      : {}),
    ...(pipe.nominalPipeLength != null
      ? { nominalPipeLength: Number(pipe.nominalPipeLength) || 0 }
      : {}),
    ...(pipe.nominalOd != null && pipe.nominalOd !== ''
      ? { nominalOd: Number(pipe.nominalOd) || 0 }
      : {}),
  };
};

/**
 * Convert pipe values from inches (database) to user's saved display units.
 * nominalPipeLength / targetPipelineLength are ft-only (no conversion).
 * API returns `units`; we support both `unit` and `units` for compatibility.
 */
export const convertSetupPipeFromInchesForDisplay = (pipe: any): any => {
  if (!pipe || typeof pipe !== 'object') return pipe;
  const u = pipe.unit || pipe.units || DEFAULT_PIPE_UNIT;
  const pfFieldUnits: Record<string, string> = {
    ...(u.pipefitterFieldUnits ?? {}),
    nominalPipeLength: 'ft',
    targetPipelineLength: 'ft',
    nominalOd: 'mm',
    hiloThreshold: 'mm',
  };
  return {
    ...pipe,
    units: { ...u, pipefitterFieldUnits: pfFieldUnits },
    pipeSize: convertSetupDimensionFromInches(Number(pipe.pipeSize) || 0, u.pipeSize || 'in'),
    wallThickness: convertSetupDimensionFromInches(Number(pipe.wallThickness) || 0, u.wallThickness || 'in'),
    pipeLength: convertValueFromInches(Number(pipe.pipeLength) || 0, u.length || 'in'),
    outputAssemblyLength: convertValueFromInches(Number(pipe.outputAssemblyLength) || 0, u.outputAssemblyLength || 'ft'),
    // Length targets: ft only — pass through as stored
    ...(pipe.targetPipelineLength != null
      ? { targetPipelineLength: Number(pipe.targetPipelineLength) || 0 }
      : {}),
    ...(pipe.nominalPipeLength != null
      ? { nominalPipeLength: Number(pipe.nominalPipeLength) || 0 }
      : {}),
    ...(pipe.nominalOd != null && pipe.nominalOd !== ''
      ? { nominalOd: Number(pipe.nominalOd) || 0 }
      : {}),
  };
};

// Get unit suffix for specific parameter
export const getParameterUnitSuffix = (parameter: string): string => {
  const displayUnit = getUnitFromSettings();
  const unitLabel = getUnitLabel(displayUnit);
  
  switch (parameter) {
    case 'travel_speed':
    case 'wire_speed':
      return `${unitLabel}/min`;
    case 'volts':
      return 'V';
    case 'current':
    case 'target':
    case 'horizontal_bias':
      return 'A';
    case 'frequency':
      return 'cyc/min';
    case 'oscillation_width':
    case 'total_wire_consumed':
    case 'band_diameter':
    case 'wire_diameter':
    case 'logging_distance':
    case 'pipe_diameter':
    case 'weld_length':
      return unitLabel;
    case 'true_energy':
    case 'heat':
      return `kJ/${unitLabel}`;
    case 'arc_impedance':
      return 'ohms';
    default:
      return unitLabel;
  }
};

// Convert fitment readings value from inches (API base) to display unit based on settings
export const convertFitmentReadingsValue = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from inches to mm
  if (displayUnit === 'mm') {
    return value * 25.4;
  }
  
  // If display unit is inches, return same value (no conversion needed)
  return Math.round(value * 100) / 100;
};

// Convert fitment readings value back to inches for API calls
export const convertFitmentReadingsValueToInches = (value: number): number => {
  if (typeof value !== 'number' || isNaN(value)) return 0;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is mm, convert from mm to inches
  if (displayUnit === 'mm') {
    return value / 25.4;
  }
  
  // If display unit is inches, return same value (no conversion needed)
  return value;
};

// Convert fitment readings object with all values from inches to display units
export const convertFitmentReadingsObject = (fitmentReadings: any): any => {
  if (!fitmentReadings || typeof fitmentReadings !== 'object') return fitmentReadings;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is inches, no conversion needed
  if (displayUnit === 'in') {
    return fitmentReadings;
  }
  
  // Parameters that need conversion from inches to mm
  const convertibleParameters = [
    'wallThicknessMin',
    'wallThicknessMax',
    'landMin',
    'landMax',
    'internalHiLowMin',
    'internalHiLowMax',
    'externalHiLowMin',
    'externalHiLowMax',
    'bevelTopRangeMin',
    'bevelTopRangeMax',
    'wallThicknessTopRangeMin',
    'wallThicknessTopRangeMax',
    'gapToleranceMin',
    'gapToleranceMax'
  ];
  
  const convertedData = { ...fitmentReadings };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertFitmentReadingsValue(convertedData[param]);
    }
  });
  
  return convertedData;
};

// Convert fitment readings object back to inches for API calls
export const convertFitmentReadingsObjectToInches = (fitmentReadings: any): any => {
  if (!fitmentReadings || typeof fitmentReadings !== 'object') return fitmentReadings;
  
  const displayUnit = getUnitFromSettings();
  
  // If display unit is inches, no conversion needed
  if (displayUnit === 'in') {
    return fitmentReadings;
  }
  
  // Parameters that need conversion from mm to inches
  const convertibleParameters = [
    'wallThicknessMin',
    'wallThicknessMax',
    'landMin',
    'landMax',
    'internalHiLowMin',
    'internalHiLowMax',
    'externalHiLowMin',
    'externalHiLowMax',
    'bevelTopRangeMin',
    'bevelTopRangeMax',
    'wallThicknessTopRangeMin',
    'wallThicknessTopRangeMax',
    'gapToleranceMin',
    'gapToleranceMax'
  ];
  
  const convertedData = { ...fitmentReadings };
  
  convertibleParameters.forEach(param => {
    if (convertedData[param] !== undefined && typeof convertedData[param] === 'number') {
      convertedData[param] = convertFitmentReadingsValueToInches(convertedData[param]);
    }
  });
  
  return convertedData;
};


// Import timezone functions from formatUtils to avoid duplication
export { 
  getTimezoneDisplayName,
  formatTimeForDisplay as formatTimestampForDisplay 
} from '../lib/formatUtils'; 




export const formatDate = (dateString: string ,isTimezone: boolean = false,isUTC: boolean = false) => {
  const date = new Date(dateString);
  
  if(isTimezone){
    if(isUTC){
      // Show UTC time format
      const day = date.getUTCDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
      const year = date.getUTCFullYear();
      const time = date.toLocaleTimeString('en-US', { 
        hour12: false, 
        timeZone: 'UTC',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
      return `${day}-${month}-${year} ${time} UTC`;
    } else {
      // Show local time format (Eastern Time, Pacific Time, etc.)
      return formatTimeForDisplay(dateString) + ' ' + getTimezoneDisplayName();
    }
  }
  
  // Default date format without time
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Convenience functions for common use cases
export const formatDateWithTime = (dateString: string, isUTC: boolean = false) => {
  return formatDate(dateString, true, isUTC);
};

export const formatDateUTC = (dateString: string) => {
  return formatDate(dateString, true, true);
};

export const formatDateLocal = (dateString: string) => {
  return formatDate(dateString, true, false);
};

/**
 * Time Formatting Examples:
 * 
 * // Basic date only (no time)
 * formatDate('2024-01-15T10:30:00Z') 
 * // Returns: "15-Jan-2024"
 * 
 * // Local time with timezone (Eastern Time, Pacific Time, etc.)
 * formatDateLocal('2024-01-15T10:30:00Z')
 * // Returns: "15-Jan-2024, 5:30 AM Eastern Time (EST)" (if EST)
 * 
 * // UTC time format
 * formatDateUTC('2024-01-15T10:30:00Z')
 * // Returns: "15-Jan-2024 10:30:00 UTC"
 * 
 * // Manual control
 * formatDate('2024-01-15T10:30:00Z', true, false) // Local time
 * formatDate('2024-01-15T10:30:00Z', true, true)  // UTC time
 */

    // Utility function to format response time
   export const formatResponseTime = (ms: number): string => {
      if (ms < 1000) {
          return `${ms}ms`;
      }

      const minutes = Math.floor(ms / 60000);
      const seconds = Math.floor((ms % 60000) / 1000);
      const milliseconds = ms % 1000;

      let result = '';
      if (minutes > 0) {
          result += `${minutes}m `;
      }
      if (seconds > 0) {
          result += `${seconds}s `;
      }
      if (milliseconds > 0) {
          result += `${milliseconds}ms`;
      }

      return result.trim();
  };